import { DateTime } from 'luxon';

const eventLabel = (event) => event.nameLong || event.nameShort || event.id;

export const STILL_IN = 'still clocked in';

const fmtFull = (ms) => DateTime.fromMillis(Number(ms)).toFormat('MMM dd hh:mm a');

// Tooltip line for one log: "Oct 10 9:10 AM - 11:40 AM" or "Oct 10 9:10 AM - still clocked in".
export const formatLogRange = (log) => {
    const start = DateTime.fromMillis(Number(log.started));
    const head = start.toFormat('MMM d h:mm a');

    if (!log.stopped) return `${ head } - ${ STILL_IN }`;

    const stop = DateTime.fromMillis(Number(log.stopped));

    return `${ head } - ${ stop.hasSame(start, 'day') ? stop.toFormat('h:mm a') : stop.toFormat('MMM d h:mm a') }`;
};

// 12300000 ms -> "3h 25m"; under an hour -> "45m".
export const formatDuration = (ms) => {
    const minutes = Math.floor(ms / 60000);
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;

    return h > 0 ? `${ h }h ${ m }m` : `${ m }m`;
};

// One row per patroller, one column per event. Same timelog fields as the Hours
// tab: log.patroller, log.started, log.stopped (ms). A cell is the summed
// (stopped - started) across all shifts of that event; an open log (no stopped)
// adds nothing but flags the cell as still clocked in.
export const buildAttendance = (patrollers, events, shiftsByEvent) => {
    const columns = events.map(eventLabel);
    const exportColumns = events.flatMap(ev => [ `${ eventLabel(ev) } Clock-in`, `${ eventLabel(ev) } Clock-out`, `${ eventLabel(ev) } Total` ]);

    const rows = patrollers.map((p) => {
        const details = events.map((event) => {
            let ms = 0;
            let open = false;
            const logs = [];

            (shiftsByEvent[ event.id ] || []).forEach((shift) => {
                (shift.timelogs || []).forEach((log) => {
                    if (log.patroller !== p.id || !log.started) return;

                    logs.push({ started: log.started, stopped: log.stopped || null });

                    if (!log.stopped) {
                        open = true;
                    } else if (log.stopped > log.started) {
                        ms += log.stopped - log.started;
                    }
                });
            });

            logs.sort((a, b) => a.started - b.started);

            return { ms, open, logs };
        });

        // Export: three columns per event (clock-in, clock-out, total).
        const exportCells = details.flatMap((d) => {
            if (d.logs.length === 0) return [ '', '', '' ];

            const closed = d.logs.filter(l => l.stopped).map(l => l.stopped);
            const lastOut = closed.length ? fmtFull(Math.max(...closed)) : '';
            const out = d.open ? [ lastOut, STILL_IN ].filter(Boolean).join(' (') + (lastOut ? ')' : '') : lastOut;

            return [ fmtFull(d.logs[ 0 ].started), out, d.ms > 0 ? formatDuration(d.ms) : '' ];
        });

        const totalMs = details.reduce((t, d) => t + d.ms, 0);

        return {
            id: p.id,
            name: `${ p.lastName }, ${ p.firstName }`,
            details,
            exportCells,
            totalMs,
            total: totalMs > 0 ? formatDuration(totalMs) : ''
        };
    });

    rows.sort((a, b) => a.name.localeCompare(b.name));

    return { columns, exportColumns, rows };
};

const csvCell = (value) => {
    const text = String(value ?? '');

    return /[",\r\n]/.test(text) ? `"${ text.replace(/"/g, '""') }"` : text;
};

export const toCsv = ({ exportColumns, rows }) => {
    const header = [ 'Patroller', ...exportColumns, 'Total Time' ];
    const lines = [ header, ...rows.map(r => [ r.name, ...r.exportCells, r.total ]) ];

    return lines.map(line => line.map(csvCell).join(',')).join('\r\n');
};

const saveBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');

    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
};

export const downloadCsv = (attendance, filename = 'attendance.csv') => {
    saveBlob(new Blob([ toCsv(attendance) ], { type: 'text/csv;charset=utf-8' }), filename);
};

// jspdf is loaded only when a PDF is requested to keep the main bundle small.
export const downloadPdf = async (attendance, filename = 'attendance.pdf') => {
    const [ { jsPDF }, { default: autoTable } ] = await Promise.all([
        import('jspdf'),
        import('jspdf-autotable')
    ]);

    const doc = new jsPDF({ orientation: 'landscape' });

    doc.setFontSize(14);
    doc.text('Event Attendance', 14, 14);
    autoTable(doc, {
        startY: 20,
        head: [ [ 'Patroller', ...attendance.exportColumns, 'Total Time' ] ],
        body: attendance.rows.map(r => [ r.name, ...r.exportCells, r.total ]),
        styles: { fontSize: 8 }
    });
    doc.save(filename);
};
